// The Men's / Women's switch on the Ranking, Filter and Stats Table (Ben, 2026-11-02: "yes, all
// three"). These modules take their gender from the canvas player (rules.md), so on a Declan Rice
// canvas the League chip lists only men's leagues and nothing inside the module reached a women's
// league. The switch opens on the player's gender; flipping it moves the module to the other
// gender's top league. A module is still one gender at a time: never a mixed group. The pure half.

import type { ActiveFilters } from "../engines/stats-filter-kit.js";
import { topLeagueLabels, type LeagueForTop } from "./top-leagues.js";

/** The switch's two sides, as a `_`-state value and its words. */
export type GenderSide = "men" | "women";
export const GENDER_LABELS: Record<GenderSide, string> = { men: "Men's", women: "Women's" };

export const sideOf = (womens: boolean): GenderSide => (womens ? "women" : "men");
export const womensOf = (side: GenderSide): boolean => side === "women";

/** The league a flip lands on: the first of Ben's top leagues of that gender that has a Stats file
 *  (Premier League ↔ WSL), else that gender's first league with one. Null = none. Pure. */
export function topLeagueOf<T extends LeagueForTop & { id: number }>(
  womens: boolean,
  choices: readonly T[],
  withFiles: ReadonlyMap<number, readonly number[]>,
): string | null {
  const own = choices.filter((c) => c.womens === womens && (withFiles.get(c.id)?.length ?? 0) > 0);
  return topLeagueLabels(own)[0] ?? own[0]?.label ?? null;
}

/** The switch's `_`-state after a flip from `shown`: the other side, or nothing (follow the player)
 *  when that's the player's own gender. Pure. */
export function pickAfterFlip(shown: boolean, playerWomens: boolean | null): GenderSide | undefined {
  const next = !shown;
  return playerWomens !== null && next === playerWomens ? undefined : sideOf(next);
}

/** The chips a flip clears: the League chip (it goes to the new gender's default, the top league
 *  above, or back to the player's) and the Club chip (that club isn't on the other side). Position,
 *  Minutes, Age, Nationality, Foot, Height and Weight are about players, not a league, so they stay.
 *  Edits `active` in place (the chip bar's own object). */
export function clearChipsForFlip(active: ActiveFilters): void {
  delete active.league;
  delete active.club;
}
